/**
 * FauxDS FilterButton - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate FilterButton component for prototyping.
 * It is NOT the real @faire/slate FilterButton and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate FilterButton, see: @faire/slate/components/filter
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 48995:1824 (Slate Filter Button)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Rest, Applied
 * - All states verified: Default, Hover, Active, Disabled
 * - Icon support verified (Filter icon)
 * - Quantity text support verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

export type FilterButtonVariant = "rest" | "applied";

export interface FilterButtonProps {
  /**
   * The label text to display
   * @default "Label"
   */
  label: string;
  /**
   * Custom icon component (ReactNode)
   * If not provided, uses default Filter icon
   */
  Icon?: React.FC<any>;
  /**
   * Accessibility label for screen readers
   * @default label
   */
  "aria-label"?: string;
  /**
   * Button variant - Rest (default) or Applied (when filter is active)
   * @default "rest"
   */
  variant?: FilterButtonVariant;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Button type attribute
   * @default "button"
   */
  type?: "button" | "submit" | "reset";

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * FilterButton - FauxDS (recreated from Figma MCP)
 *
 * Filter Buttons are used to expose a menu of filtering options.
 * They're typically used as a trigger to open a Menu, Modal, Side Panel, or other component.
 *
 * Usage tips:
 * - Use a clear and identifiable icon to represent the content beneath the trigger
 * - Use a short, descriptive, single word for the label
 * - Don't use this as a single, toggleable Filter element
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  (
    {
      label,
      Icon,
      "aria-label": accessibilityLabel,
      variant = "rest",
      disabled = false,
      onClick,
      type = "button",
      className,
    },
    ref
  ) => {
    const ariaLabel = accessibilityLabel || label;

    // Determine visual state styles based on Figma design tokens
    const getStateStyles = () => {
      if (disabled) {
        return {
          bg: "bg-(--slate-color-action-surface-disabled)", // #f7f7f7
          border: "border-(--slate-color-action-border-subdued)", // #dfe0e1
          text: "text-(--slate-color-action-text-disabled)", // #757575
          icon: "text-(--slate-color-action-icon-disabled)", // #757575
        };
      }

      // Applied variant (dark background)
      if (variant === "applied") {
        return {
          bg: "bg-(--slate-color-action-surface-default) hover:bg-(--slate-color-action-surface-hover) active:bg-(--slate-color-action-surface-active)", // #333333
          border:
            "border-(--slate-color-action-surface-default) hover:border-(--slate-color-action-border-hover) active:border-(--slate-color-action-border-active)", // #333333
          text: "text-(--slate-color-action-text-inverse) hover:text-(--slate-color-action-text-inverse) active:text-(--slate-color-action-text-inverse)", // #ffffff
          icon: "text-(--slate-color-action-icon-inverse) hover:text-(--slate-color-action-icon-inverse) active:text-(--slate-color-action-icon-inverse)", // #ffffff
        };
      }

      return {
        bg: "bg-(--slate-color-action-surface-inverse) hover:bg-(--slate-color-action-surface-inverse) active:bg-(--slate-color-action-surface-inverse)", // #ffffff
        border:
          "border-(--slate-color-action-border-subdued) hover:border-(--slate-color-action-border-hover) active:border-(--slate-color-action-border-active)", // #dfe0e1
        text: "text-(--slate-color-action-text-default) hover:text-(--slate-color-action-text-hover) active:text-(--slate-color-action-text-active)", // #333333
        icon: "text-(--slate-color-action-icon-default) hover:text-(--slate-color-action-icon-hover) active:text-(--slate-color-action-icon-active)", // #333333
      };
    };

    const stateStyles = getStateStyles();

    // Icon size (16px from Figma)
    const iconSize = 16;

    return (
      <button
        ref={ref}
        type={type}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(
          "inline-flex h-[40px] items-center justify-center gap-2 rounded-full border px-3 transition-colors duration-150 ease-in-out focus:outline-hidden focus-visible:ring-2 focus-visible:ring-(--slate-color-action-border-focus-ring) focus-visible:ring-offset-2 disabled:cursor-not-allowed",
          stateStyles.bg,
          stateStyles.border,
          stateStyles.text,
          className
        )}
        style={{
          borderRadius: "var(--slate-radius-component-round)",
          fontFamily: "var(--slate-typography-family-sans-serif)",
          fontSize: "var(--slate-typography-font-size-200)",
          fontWeight: "var(--slate-typography-weight-regular)",
          lineHeight: "var(--slate-typography-line-height-200)",
          letterSpacing: "var(--slate-typography-letter-spacing-200)",
        }}
      >
        {/* Icon */}
        {Icon && (
          <span
            className={cn(
              "flex shrink-0 items-center justify-center",
              stateStyles.icon
            )}
            style={{ width: iconSize, height: iconSize }}
          >
            <Icon />
          </span>
        )}

        {/* Label */}
        <span className={stateStyles.text}>{label}</span>
      </button>
    );
  }
);

FilterButton.displayName = "FilterButton";

export default FilterButton;
